import { WritableDraft, createDraft, finishDraft } from "immer";

import { yieldingIterable } from "../async.js";
import { DateTimeString } from "../dateTypes.js";
import { AppRunState, CellExecutionState, OutputType } from "../enums.js";
import {
  AppSessionCellId,
  CellId,
  OutputContentId,
  OutputId,
} from "../idTypeBrands.js";
import { ScopeItem } from "../scopeTypes.js";
import { typedObjectFromEntries, typedObjectValues } from "../utils/index.js";

import {
  AppSessionEvent,
  ExhaustiveAppSessionEvent,
} from "./AppSessionEvent.js";

export interface AppSession {
  state: AppRunState;
  totalMemory: string | null;
  availableMemory: string | null;
  maxMemory: string | null;
  lastRunStart: DateTimeString | null;
  lastRunEnd: DateTimeString | null;
  /**
   * Incremented everytime this app session gets run.
   * Used to determine if a cell execution is 'stale' relative to any parent cells being run.
   *
   * If not present, should be treated as `0`.
   */
  executionRevision: number | undefined;
}

export interface AppSessionCell {
  id: AppSessionCellId;
  cellId: CellId;
  state: CellExecutionState;
  lastExecutionStartTime: DateTimeString | null;
  lastExecutionEndTime: DateTimeString | null;
  /**
   * The last `Cell.sourceRevision` this cell was run with.
   *
   * If not present, this cell has not been run
   */
  lastRunSourceRevision: number | null | undefined;
  /**
   * The last `AppSessionCellDB.stateRevision` this cell was run with.
   *
   * If not present, this cell has not been run
   */
  lastRunStateRevision: number | null | undefined;
  /**
   * The last `AppSession.lastRunExecutionRevision` this cell was run with.
   *
   * If not present, this cell has not been run
   */
  lastRunExecutionRevision: number | null | undefined;
}

export interface Output {
  id: OutputId;
  appSessionCellId: AppSessionCellId;
  // Order is 1-indexed
  order: number;
  outputType: OutputType;
  requestId: string | null;
}

export interface OutputContent {
  id: OutputContentId;
  outputId: OutputId;
  createdDate: DateTimeString;
  mimeType: string;
}

/**
 * Additional state used to help with update performance
 * This is usually constructed just in time.
 * This is not stored to minimize total storage space.
 */
export interface AppSessionEventStateIndices {
  cellIdToAppSessionCellId: Record<CellId, AppSessionCellId | undefined>;
  appSessionCellIdToOutputIds: Record<AppSessionCellId, OutputId[] | undefined>;
  outputIdToOutputContentIds: Record<OutputId, OutputContentId[] | undefined>;
}

/**
 * Represents a compacted series of app session events
 */
export interface AppSessionEventState {
  appSession: AppSession;
  appSessionCells: Record<AppSessionCellId, AppSessionCell>;
  outputs: Record<OutputId, Output>;
  outputContents: Record<OutputContentId, OutputContent>;
  /** Indexed by name */
  scopeItems: Record<string, ScopeItem>;
}

/**
 * Create an empty, initial version of an app session state.
 */
export function createAppSessionEventState(): AppSessionEventState {
  return {
    appSession: {
      state: AppRunState.IDLE,
      totalMemory: null,
      availableMemory: null,
      maxMemory: null,
      lastRunStart: null,
      lastRunEnd: null,
      executionRevision: 0,
    },
    appSessionCells: {},
    outputs: {},
    outputContents: {},
    scopeItems: {},
  };
}

export function createAppSessionEventStateIndices(
  state: AppSessionEventState,
): AppSessionEventStateIndices {
  return {
    cellIdToAppSessionCellId: typedObjectFromEntries(
      typedObjectValues(state.appSessionCells).map((appSessionCell) => [
        appSessionCell.cellId,
        appSessionCell.id,
      ]),
    ),
    appSessionCellIdToOutputIds: typedObjectValues(state.outputs).reduce<
      Record<AppSessionCellId, OutputId[]>
    >((acc, output) => {
      (acc[output.appSessionCellId] ??= []).push(output.id);
      return acc;
    }, {}),
    outputIdToOutputContentIds: typedObjectValues(state.outputContents).reduce<
      Record<OutputId, OutputContentId[]>
    >((acc, outputContent) => {
      (acc[outputContent.outputId] ??= []).push(outputContent.id);
      return acc;
    }, {}),
  };
}

/**
 * Take a state and series of events to produce a new state that has all the changes
 * applied.
 */
export function produceAppSessionEventState({
  events,
  indices,
  state,
}: {
  state: AppSessionEventState;
  indices: AppSessionEventStateIndices;
  events: readonly ExhaustiveAppSessionEvent[];
}): {
  newState: AppSessionEventState;
  newIndices: AppSessionEventStateIndices;
  warnings: string[];
} {
  const draft = createDraft({ state, indices });

  const result = updateDraftAppSessionEventState({
    state: draft.state,
    indices: draft.indices,
    events,
  });

  const { indices: newIndices, state: newState } = finishDraft(draft);

  return { newState, newIndices, warnings: result.warnings };
}

/**
 * Async version of @see produceAppSessionEventState that avoids
 * blocking the event loop
 */
export async function produceAppSessionEventStateAsync({
  events,
  indices,
  state,
}: {
  state: AppSessionEventState;
  indices: AppSessionEventStateIndices;
  events: readonly ExhaustiveAppSessionEvent[];
}): Promise<{
  newState: AppSessionEventState;
  newIndices: AppSessionEventStateIndices;
  warnings: string[];
}> {
  const warnings: string[] = [];
  const draft = createDraft({ state, indices });

  for await (const event of yieldingIterable(events, 50)) {
    const result = updateDraftAppSessionEventState({
      state: draft.state,
      indices: draft.indices,
      events: [event],
    });
    warnings.push(...result.warnings);
  }

  const { indices: newIndices, state: newState } = finishDraft(draft);

  return { newState, warnings, newIndices };
}

/**
 * Mutates a draft state by applying a series of events to it.
 */
export function updateDraftAppSessionEventState({
  events,
  indices,
  state,
}: {
  state: WritableDraft<AppSessionEventState>;
  indices: WritableDraft<AppSessionEventStateIndices>;
  events: readonly ExhaustiveAppSessionEvent[];
}): { warnings: string[] } {
  const warningAggregator: string[][] = [];
  for (const event of events) {
    const eventDefinition = AppSessionEvent[event.type];

    if (eventDefinition == null) {
      warningAggregator.push([
        `Unknown event type: ${event.type}, continuing but might cause unexpected behavior`,
      ]);
      continue;
    }

    const result = AppSessionEvent[event.type].apply({
      state,
      indices,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- we know that this payload type matches the handler type
      payload: event.payload as any,
    });

    if (result?.warnings != null) {
      warningAggregator.push(
        result.warnings.map((warning) => `${event.type} - ${warning}`),
      );
    }
  }

  return { warnings: warningAggregator.flat() };
}
